import { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { clsx } from 'clsx'
import { History, Loader2, ChevronLeft, ChevronRight, ArrowLeft, Globe, ExternalLink, ArrowRight } from 'lucide-react'
import api from '../lib/api'
import { useSeoHead } from '../lib/seo'

const PAGE_SIZE = 24
const API_BASE = (api.defaults.baseURL as string) || '/tools/api/public/api/v1'
const shot = (domain: string) => `${API_BASE}/analyses/screenshot?url=${encodeURIComponent('https://' + domain)}&w=220&h=136`

export default function RecentAnalysesPage() {
  const { t } = useTranslation()
  useSeoHead({
    title: t('recent.meta_title', { defaultValue: 'Nedávné SEO analýzy webů | SEO Tools' }),
    description: t('recent.meta_desc', { defaultValue: 'Přehled nedávno analyzovaných webů a jejich SEO skóre. Inspirujte se, jak si vedou jiné weby, a zanalyzujte ten svůj zdarma.' }),
    canonicalPath: '/nedavne-analyzy',
  })
  const [page, setPage] = useState(0)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['recent-all', page],
    queryFn: () => api.get(`/analyses/recent?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  })

  const analyses: any[] = data?.analyses ?? []
  const total: number = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 mb-5 transition">
        <ArrowLeft className="w-4 h-4" /> {t('common.back')}
      </Link>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <History className="w-6 h-6" /> {t('home.recent')}
        </h1>
        {total > 0 && <span className="text-sm text-gray-400">{total} {t('home.recent_sites')}</span>}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary-400" /></div>
      ) : analyses.length === 0 ? (
        <div className="text-center py-20 text-gray-400">{t('history.empty')}</div>
      ) : (
        <>
          <div className={clsx('bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 overflow-hidden divide-y divide-gray-50 dark:divide-white/5 transition-opacity', isFetching && 'opacity-60')}>
            {analyses.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.3) }}
                className="flex items-stretch gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5 transition group"
              >
                {/* Náhled → interní stránka s analýzou */}
                <Link to={`/results/${a.domain}`} className="relative w-[110px] h-[68px] shrink-0 rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5">
                  <img src={shot(a.domain)} alt={`${a.title || a.domain} – náhled`} loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.04]"
                    onError={(e) => { e.currentTarget.style.display = 'none' }} />
                  <Globe className="absolute inset-0 m-auto w-6 h-6 text-gray-200 -z-10" />
                </Link>
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  {/* Titulek = skutečný zpětný odkaz na web (anchor text = title) */}
                  <h2 className="text-sm font-semibold leading-snug truncate">
                    <a href={`https://${a.domain}`} target="_blank" rel="noopener"
                      className="inline-flex items-center gap-1 text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition">
                      <span className="truncate">{a.title || a.domain}</span>
                      <ExternalLink className="w-3 h-3 shrink-0 text-gray-300 group-hover:text-primary-400 transition" />
                    </a>
                  </h2>
                  {a.description
                    ? <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{a.description}</p>
                    : <p className="text-xs text-emerald-600 dark:text-emerald-400 truncate mt-0.5">https://{a.domain}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-gray-400">{new Date(a.created_at).toLocaleDateString('cs-CZ')}</span>
                    <Link to={`/results/${a.domain}`} className="text-[11px] font-semibold text-primary-600 hover:text-primary-700 inline-flex items-center gap-0.5">
                      {t('home.recent_view')} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
                <div className="flex flex-col items-end justify-center shrink-0">
                  <span className={clsx('text-2xl font-extrabold',
                    a.score >= 80 ? 'text-emerald-500' : a.score >= 50 ? 'text-amber-500' : 'text-red-500')}>{a.score}</span>
                  <span className="text-[10px] text-gray-400">/ 100</span>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Stránkování */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="p-2 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 transition">
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: pages }).slice(Math.max(0, page - 2), Math.max(0, page - 2) + 5).map((_, idx) => {
                const p = Math.max(0, page - 2) + idx
                return (
                  <button key={p} onClick={() => setPage(p)}
                    className={clsx('w-9 h-9 rounded-lg text-sm font-semibold transition',
                      p === page ? 'bg-primary-600 text-white' : 'border border-gray-200 dark:border-white/10 text-gray-600 hover:bg-gray-50 dark:hover:bg-white/5')}>
                    {p + 1}
                  </button>
                )
              })}
              <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
                className="p-2 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 transition">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
